import { FC, ReactNode, useMemo } from 'react'

import { match } from 'ts-pattern'
import {
  Text,
  IconCalendar,
  IconCheckmark,
  IconClock,
  IconEmail,
  IconFlag,
  IconCircleBanSign,
  GreyGrey70,
  Primary13Regular,
  Tooltip
} from '@sequencehq/core-components'

import { format } from '@sequencehq/utils/dates'

import { NodeTimeline } from 'Automations/components'
import { ExecutionSummaryComponent } from 'Automations/nodeDefinitions/types'
import {
  CancelledItem,
  FailedItem,
  TerminalStateItem
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import {
  ReminderEmailPreview,
  ReminderEmailPreviewInvoice
} from './components/ReminderEmailPreview'
import { ReminderEmailRecipients } from './components/ReminderEmailRecipients'
import {
  ReminderEmailData,
  useReminderData
} from './Reminder.executionSummary.hooks'
import { formatSendReminder } from './helpers'
import type { Config, SendReminder } from '.'

export const ReminderExecutionSummary: ExecutionSummaryComponent = ({
  tasks,
  automationExecution,
  isLatestNodeExecution
}) => {
  const { state, config, emailData } = useReminderData({
    tasks,
    automationExecution
  })
  const timestamp = automationExecution.createTime

  const invoice = useMemo<ReminderEmailPreviewInvoice | undefined>(() => {
    const { invoiceId, invoiceNumber } = automationExecution.input
    if (typeof invoiceId !== 'string' || typeof invoiceNumber !== 'string') {
      return undefined
    }
    return { id: invoiceId, invoiceNumber }
  }, [automationExecution.input])

  return match(state)
    .with({ type: 'INVOICE_PAID' }, () => (
      <TerminalStateItem
        icon={<IconCheckmark />}
        label="Invoice paid"
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .with({ type: 'INVOICE_UNCOLLECTIBLE' }, () => (
      <TerminalStateItem
        icon={<IconCircleBanSign />}
        label="Invoice uncollectible"
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .with({ type: 'CANCELLED' }, () => (
      <CancelledItem
        label="Payment reminder cancelled"
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .with({ type: 'FAILED' }, () => (
      <FailedItem
        label="Payment reminder failed"
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .with({ type: 'SENT' }, () => (
      <EmailItem
        icon={<IconCalendar />}
        label="Payment reminder sent"
        config={config}
        emailData={emailData}
        invoice={invoice}
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .with({ type: 'SENDING' }, () => (
      <EmailItem
        icon={<IconEmail />}
        label="Sending payment reminder"
        config={config}
        emailData={emailData}
        invoice={invoice}
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .with({ type: 'IN_REVIEW' }, ({ isReplacementFailure }) => (
      <EmailItem
        icon={<IconFlag />}
        label="Payment reminder flagged for review"
        description={
          isReplacementFailure
            ? 'There was an issue substituting some of the template variables in the email, please review before sending'
            : undefined
        }
        config={config}
        emailData={emailData}
        invoice={invoice}
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .with({ type: 'SKIPPED' }, () => (
      <NodeTimeline.Item
        icon={<IconCircleBanSign />}
        showStem={!isLatestNodeExecution}
      >
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label="Payment reminder skipped"
            timestamp={timestamp}
          />
        </NodeTimeline.Title>
        <Text
          {...Primary13Regular}
          color={GreyGrey70}
          marginBottom="8px"
          data-testid="automations.executionSummary.reminder.skippedDescription"
        >
          This reminder was scheduled for on or before the day the invoice was
          sent, so it was not sent.
        </Text>
      </NodeTimeline.Item>
    ))
    .with({ type: 'WAITING' }, ({ waitUntil }) => (
      <NodeTimeline.Item icon={<IconClock />} showStem={!isLatestNodeExecution}>
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label={
              <WaitingLabel
                sendReminder={config?.sendReminder}
                waitUntil={waitUntil}
              />
            }
            timestamp={timestamp}
          />
        </NodeTimeline.Title>
      </NodeTimeline.Item>
    ))
    .with({ type: 'PREPARING' }, () => (
      <TerminalStateItem
        icon={<IconEmail />}
        label="Preparing payment reminder"
        timestamp={timestamp}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    ))
    .exhaustive()
}

const EmailItem: FC<{
  icon: ReactNode
  label: string
  config: Config | undefined
  emailData: ReminderEmailData
  invoice: ReminderEmailPreviewInvoice | undefined
  timestamp: string
  description?: string
  isLatestNodeExecution?: boolean
}> = ({
  icon,
  label,
  config,
  emailData,
  invoice,
  timestamp,
  description,
  isLatestNodeExecution
}) => (
  <NodeTimeline.Item icon={icon} showStem={!isLatestNodeExecution}>
    <NodeTimeline.Title>
      <NodeTimeline.UserAction
        label={label}
        timestamp={timestamp}
        inspectorDetail={
          config && (
            <ReminderEmailRecipients config={config} emailData={emailData} />
          )
        }
      />
    </NodeTimeline.Title>
    {description && (
      <Text
        {...Primary13Regular}
        color={GreyGrey70}
        marginBottom="8px"
        data-testid="automations.executionSummary.reminder.description"
      >
        {description}
      </Text>
    )}
    {config && (
      <ReminderEmailPreview
        config={config}
        emailData={emailData}
        invoice={invoice}
        startCollapsed={!isLatestNodeExecution}
      />
    )}
  </NodeTimeline.Item>
)

const WaitingLabel: FC<{
  sendReminder: SendReminder | undefined
  waitUntil: Date | null
}> = ({ sendReminder, waitUntil }) => {
  if (!sendReminder) {
    return <>Waiting to create payment reminder</>
  }

  const durationLabel = formatSendReminder(sendReminder)

  if (!waitUntil) {
    return <>Waiting until {durationLabel} to create payment reminder</>
  }

  return (
    <>
      Waiting until{' '}
      <Tooltip label={format(waitUntil, 'd MMM yyyy')}>
        <Text
          as="span"
          {...Primary13Regular}
          textDecoration="underline"
          textDecorationStyle="dotted"
          data-testid="automations.executionSummary.reminder.waitDuration"
        >
          {durationLabel}
        </Text>
      </Tooltip>{' '}
      to create payment reminder
    </>
  )
}
